import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DateField, FormErrorSummary, SelectField, TextField, TextareaField, applyServerErrors, useConfigLabel, zRequiredString } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';
import { todayInDubai } from '@/shared/lib/dates';

import { useClassifyEmployer } from '../api/mutations';
import { employerCategoriesQuery, lookupsQuery } from '../api/queries';

const schema = z.object({ bankId: zRequiredString(), categoryCode: zRequiredString(), effectiveFrom: zRequiredString(), bankEmployerCode: z.string().optional(), note: z.string().optional() });
type Values = z.infer<typeof schema>;

/** Adds an effective-dated classification at a bank; the current one ends the day before. */
export function ClassifyDialog({ employer, onClose }: { employer: Schema<'Employer'>; onClose: () => void }) {
  const { t } = useTranslation('employers');
  const configLabel = useConfigLabel();
  const lookups = useQuery(lookupsQuery);
  const classify = useClassifyEmployer(employer.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { bankId: '', categoryCode: '', effectiveFrom: todayInDubai(), bankEmployerCode: '', note: '' } });
  const bankId = useWatch({ control: form.control, name: 'bankId' });
  const categories = useQuery({ ...employerCategoriesQuery(bankId), enabled: Boolean(bankId) });
  const current = employer.classifications.find((c) => c.bankId === bankId);
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await classify.mutateAsync({ bankId: v.bankId, categoryCode: v.categoryCode, effectiveFrom: v.effectiveFrom, ...(v.bankEmployerCode?.trim() ? { bankEmployerCode: v.bankEmployerCode.trim() } : {}), ...(v.note?.trim() ? { note: v.note.trim() } : {}) });
      toast.done(t('classify.done'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['categoryCode', 'effectiveFrom']);
        setProblem(e);
      }
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={t('classify.title', { name: employer.name })}
        description={t('classify.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={classify.isPending} onClick={() => void submit()}>
              {t('classify.save')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          <div className="grid grid-cols-2 gap-4">
            <SelectField control={form.control} name="bankId" label={t('classify.bank')} options={(lookups.data?.banks ?? []).map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} />
            <SelectField control={form.control} name="categoryCode" label={t('classify.category')} disabled={!categories.data?.length} options={(categories.data ?? []).map((c) => ({ value: c.code, label: c.listed ? configLabel(c.label, c.labelAr) : t('classify.unlistedOption', { label: configLabel(c.label, c.labelAr) }) }))} placeholder={bankId && !categories.data?.length ? t('classify.noCategories') : t('form.selectPlaceholder', { ns: 'common' })} />
          </div>
          {current ? <p className="text-meta text-ink-secondary">{t('classify.current', { category: current.categoryLabel })}</p> : null}
          <div className="grid grid-cols-2 gap-4">
            <DateField control={form.control} name="effectiveFrom" label={t('classify.effectiveFrom')} />
            <TextField control={form.control} name="bankEmployerCode" label={t('classify.bankCode')} optional dir="ltr" />
          </div>
          <TextareaField control={form.control} name="note" label={t('classify.note')} optional rows={2} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
